/**
 * UnifiedPropertySelector Component
 *
 * Generic property selector that works for ALL user roles:
 * - Homeowners (viewer): Shows their owned properties
 * - Service Providers (technician): Shows assigned properties
 * - Employees (employee): Shows assigned properties
 * - Members: Shows properties assigned to them (via RLS)
 * - Admins: Shows all properties (or nothing, depending on context)
 *
 * This component automatically detects the user's role and fetches
 * the appropriate properties without role-specific logic in consuming components.
 */

import React from 'react';
import { Home, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getProfileRole } from '@/services/auth/auth.service';
import { homeOwnerObjectsService } from '@/services/supabase/homeOwnerObjects.service';
import { propertyAssignmentsService } from '@/services/supabase/propertyAssignments.service';
import { employeesService } from '@/services/supabase/employees.service';
import { useSelectedProperty } from '../hooks/useSelectedProperty';
import styles from './PropertySelector.module.css';

export const UnifiedPropertySelector = ({ className, showIcon = false }: { className?: string; showIcon?: boolean }) => {
  const { t } = useTranslation();
  const { selectedPropertyId, setSelectedPropertyId, setSelectedPropertyName } = useSelectedProperty();
  const [userRole, setUserRole] = React.useState<'admin' | 'technician' | 'viewer' | 'employee' | null>(null);

  // Fetch user role
  React.useEffect(() => {
    const fetchRole = async () => {
      const role = await getProfileRole();
      setUserRole(role);
    };
    fetchRole();
  }, []);

  // Fetch properties based on role - generic approach
  const { data: properties = [], isLoading, error } = useQuery({
    queryKey: ['unified-properties', userRole],
    queryFn: async () => {
      if (!userRole) {
        console.log('[UnifiedPropertySelector] No user role, returning empty array');
        return [];
      }

      console.log('[UnifiedPropertySelector] Fetching properties for role:', userRole);

      // Employees see their own assigned properties from employee_object_assignments
      if (userRole === 'employee') {
        console.log('[UnifiedPropertySelector] Fetching employee assigned properties...');
        const employeeProperties = await employeesService.getAssignedPropertiesForEmployee();
        console.log('[UnifiedPropertySelector] Employee properties fetched:', employeeProperties);
        return employeeProperties;
      }

      // Service providers see assigned properties via serviceproviderpermissions
      if (userRole === 'technician') {
        console.log('[UnifiedPropertySelector] Fetching service provider assigned properties...');
        const spProperties = await propertyAssignmentsService.getAssignedPropertiesForServiceProvider();
        console.log('[UnifiedPropertySelector] Service provider properties fetched:', spProperties);
        return spProperties;
      }

      // Homeowners, members, and admins see properties via homeOwnerObjectsService
      // RLS policies will automatically filter to show:
      // - Homeowners: Their owned properties
      // - Members: Properties assigned to them
      // - Admins: All properties (if RLS allows)
      console.log('[UnifiedPropertySelector] Fetching homeowner properties...');
      const homeownerProperties = await homeOwnerObjectsService.getAll();
      console.log('[UnifiedPropertySelector] Homeowner properties fetched:', homeownerProperties);
      return homeownerProperties;
    },
    enabled: !!userRole,
    onError: (err) => {
      console.error('[UnifiedPropertySelector] Error fetching properties:', err);
    },
  });

  React.useEffect(() => {
    // Verify the selected property exists in the fetched list
    const propertyExists = properties.some(p => p.id === selectedPropertyId);

    // Auto-select first property if:
    // 1. No property is selected, OR
    // 2. Selected property doesn't exist in the fetched list (stale localStorage)
    if (properties.length > 0 && (!selectedPropertyId || !propertyExists)) {
      console.log('[UnifiedPropertySelector] Auto-selecting first property:', properties[0].id, properties[0].name);
      console.log('[UnifiedPropertySelector] Previous selectedPropertyId:', selectedPropertyId, 'existed in list:', propertyExists);
      setSelectedPropertyId(properties[0].id);
      setSelectedPropertyName(properties[0].name);
    }
  }, [properties, selectedPropertyId, setSelectedPropertyId, setSelectedPropertyName]);

  React.useEffect(() => {
    console.log('[UnifiedPropertySelector] Current state:', {
      userRole,
      propertiesCount: properties.length,
      selectedPropertyId,
      isLoading,
      error: error?.message,
    });
  }, [userRole, properties, selectedPropertyId, isLoading, error]);

  const handlePropertyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const propertyId = e.target.value;
    const selectedProperty = properties.find(p => p.id === propertyId);
    setSelectedPropertyId(propertyId);
    setSelectedPropertyName(selectedProperty?.name || null);
  };

  if (isLoading) {
    return (
      <div className={`${styles.selectorContainer} ${styles.loading} ${className || ''}`}>
        <Loader2 className={styles.spinner} size={16} />
        <span>{t('features.properties.loading', 'Loading properties...')}</span>
      </div>
    );
  }

  if (error) {
    return <div className={`${styles.selectorContainer} ${styles.error} ${className || ''}`}>{t('features.properties.errorLoading', 'Error loading properties')}</div>;
  }

  if (!properties || properties.length === 0) {
    return <div className={`${styles.selectorContainer} ${styles.noProperties} ${className || ''}`}>{t('features.properties.noProperties', 'No properties available')}</div>;
  }

  return (
    <div className={`${styles.selectorContainer} ${className || ''}`}>
      <div className={styles.selectWrapper}>
        {showIcon && <Home size={16} className={styles.icon} />}
        <select
          id="property-selector"
          value={selectedPropertyId || ''}
          onChange={handlePropertyChange}
          className={styles.select}
        >
          <option value="" disabled>{t('features.properties.selectProperty', 'Select a property...')}</option>
          {properties.map((property) => (
            <option key={property.id} value={property.id}>
              {property.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

